import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../../components/ui/sheet';
import { Plus, Edit, Trash2, Building2, Mail, Phone, MapPin, Search, RotateCcw, Pencil, AlertTriangle, X , List, LayoutGrid } from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';
import ThemeToggle from '../../../components/ThemeToggle';
import { Skeleton } from '../../../components/ui/skeleton';
import { usePermissions } from '../../../hooks/usePermissions';

export default function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, account: null });
  const [editDialog, setEditDialog] = useState({ isOpen: false, account: null });
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gstNumber: '',
    status: 'active'
  });
  const { get, put, delete: deleteApi } = useApi();
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const { can, PERMISSIONS } = usePermissions();

  useEffect(() => {
    fetchAccounts();
  }, []);

  // Set default view mode based on screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobile = window.innerWidth < 768; // md breakpoint
      setViewMode(isMobile ? 'grid' : 'table');
    };

    // Set initial view mode
    checkScreenSize();

    // Listen for resize events
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleRefresh = () => {
    fetchAccounts();
  };


  const fetchAccounts = async () => {
    try {
      setIsLoading(true);
      
      // api calling
      const response = await get('/accounts');
      
      
      // Handle the new API response structure
      if (response?.success && Array.isArray(response?.data)) {
        setAccounts(response.data);
      } else {
        setAccounts([]);
      }
      
    } catch (error) {
      
      let errorMessage = 'An error occurred while fetching accounts. Please try again.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please login again.';
        // Redirect to login or handle auth failure
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.status === 404) {
        errorMessage = 'API endpoint not found. Please check the server configuration.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      setAccounts([]); // Set empty array on error to prevent loading state
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteDialog = (account) => {
    setDeleteDialog({ isOpen: true, account });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ isOpen: false, account: null });
  };

  const openEditDialog = (account) => {
    setEditDialog({ isOpen: true, account });
    setEditFormData({
      name: account.name || '',
      email: account.email || '',
      phone: account.phone || '',
      gstNumber: account.gstNumber || account.gst_number || '', // Handle both field names
      status: account.status || 'active'
    });
  };

  const closeEditDialog = () => {
    setEditDialog({ isOpen: false, account: null });
    setEditFormData({
      name: '',
      email: '',
      phone: '',
      gstNumber: '',
      status: 'active'
    });
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateAccount = async () => {
    if (!editDialog.account) return;
    
    try {
      // Map the form data to match the API expected format
      const apiPayload = {
        name: editFormData.name,
        email: editFormData.email,
        phone: editFormData.phone,
        gst_number: editFormData.gstNumber, // Map gstNumber to gst_number
        status: editFormData.status
      };
      
      
      const response = await put(`/accounts/${editDialog.account._id}`, apiPayload);
      
      
      // Update the local accounts array with the new data
      setAccounts(prevAccounts => 
        prevAccounts.map(account => 
          account._id === editDialog.account._id 
            ? { ...account, ...editFormData }
            : account
        )
      );
      
      toast.success('Account updated successfully');
      closeEditDialog();
    } catch (error) {
      
      let errorMessage = 'An error occurred while updating the account. Please try again.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteDialog.account) return;
    
    try {
      
      const response = await deleteApi(`/accounts/${deleteDialog.account._id}`);
      
      
      // Remove the account from the local accounts array
      setAccounts(prevAccounts => 
        prevAccounts.filter(account => account._id !== deleteDialog.account._id)
      );
      
      toast.success('Account deleted successfully');
      closeDeleteDialog();
    } catch (error) {
      
      let errorMessage = 'An error occurred while deleting the account. Please try again.';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error (500). The server encountered an unexpected condition.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  // Filter accounts based on search term
  const filteredAccounts = accounts.filter(account =>
    account.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.phone?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredAccounts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAccounts = filteredAccounts.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Skeleton loading component
  const SkeletonRow = () => (
    <TableRow className="h-8">
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-4 w-20" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-4 w-24" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2 hidden sm:table-cell">
        <Skeleton className="h-4 w-32" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-4 w-24" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2 hidden md:table-cell">
        <Skeleton className="h-4 w-20" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <Skeleton className="h-6 w-16 rounded-full" />
      </TableCell>
      <TableCell className="px-2 sm:px-3 py-2">
        <div className="flex items-center gap-0.5 sm:gap-1">
          <Skeleton className="h-6 w-6 sm:h-8 sm:w-8 rounded" />
          <Skeleton className="h-6 w-6 sm:h-8 sm:w-8 rounded" />
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="p-2 sm:p-3 lg:p-4 max-w-full overflow-hidden bg-background text-foreground min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-4">
        <h1 className="text-xl sm:text-2xl font-bold">Accounts</h1>
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto flex-wrap">
         
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            className="flex items-center gap-2 flex-shrink-0  border-0 bg-muted/50"
          >
            <RotateCcw className="h-4 w-4 " />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')}
            className="flex items-center gap-2 flex-shrink-0 border-0 bg-muted/50"
          >
            {viewMode === 'table' ? <LayoutGrid className="h-4 w-4" /> : <List className="h-4 w-4" />}
            <span className="hidden sm:inline">{viewMode === 'table' ? 'Grid' : 'Table'}</span>
          </Button>
        </div>
      </div>

      {/* Table or Grid View */}
      {viewMode === 'table' ? (
      <div className="bg-card text-card-foreground rounded-sm overflow-x-auto">
        <Table className="text-xs sm:text-sm min-w-full border-1 border-muted-foreground/20">
      <TableHeader className="bg-muted/70 border-1 border-muted-foreground/20">
            <TableRow className="h-8 bg-muted/50">
            <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[120px]">Date</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[120px] sm:min-w-[140px]">Company</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[140px] sm:min-w-[170px] hidden sm:table-cell">Email</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[100px] sm:min-w-[120px]">Phone</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[120px] hidden md:table-cell">GST</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[100px]">Status</TableHead>
              <TableHead className="px-2 sm:px-3 py-2 text-xs sm:text-sm min-w-[80px] sm:min-w-[100px]">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
            {isLoading ? (
              // Show skeleton rows while loading
              Array.from({ length: 5 }).map((_, index) => (
                <SkeletonRow key={`skeleton-${index}`} />
              ))
            ) : (
              // Show actual data when loaded
              currentAccounts.map((account) => (
                <TableRow key={account._id} className="h-8">
                  <TableCell className="px-2 sm:px-3 py-2 cursor-pointer hover:text-primary">
                    <span className="text-xs sm:text-sm ">
                      {new Date(account.createdAt).toLocaleDateString('en-GB')}
                    </span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2">
                    <span className="text-xs sm:text-sm truncate max-w-[100px] sm:max-w-[120px]">{account.name}</span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2 hidden sm:table-cell">
                    <span className="text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[140px]">{account.email?.toLowerCase()}</span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2">
                    <span className="text-xs sm:text-sm">{account.phone}</span>
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2  text-xs sm:text-sm hidden md:table-cell">
                    {account.gstNumber || account.gst_number || 'N/A'}
                  </TableCell>
                  <TableCell className="px-2 sm:px-3 py-2">
                    <span className={`px-1 py-0.5 rounded text-xs sm:text-sm font-medium ${
                      account.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' :
                      account.status === 'inactive' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100' :
                      'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100'
                    }`}>
                      {account.status || 'Inactive'}
                    </span>
                  </TableCell>
                 
                  <TableCell className="px-2 sm:px-3 py-2">
                    <div className="flex items-center gap-0.5 sm:gap-1">
                      {can(PERMISSIONS.EDIT_ACCOUNT) && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openEditDialog(account)}
                          className="h-6 w-6 sm:h-8 sm:w-8 p-0"
                        >
                          <Pencil className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      )}
                      {can(PERMISSIONS.DELETE_ACCOUNT) && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openDeleteDialog(account)}
                          className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
      </TableBody>
    </Table>
      </div>
      ) : (
        /* Grid View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {isLoading ? (
            /* Skeleton Grid */
            Array.from({ length: 8 }).map((_, index) => (
              <div key={`skeleton-${index}`} className="bg-card text-card-foreground rounded-sm overflow-x-auto border-1 border-muted-foreground/20">
                <div className="p-4 space-y-3">
                  <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-6 w-16 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
                </div>
              </div>
            ))
          ) : currentAccounts.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <Building2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No accounts found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm ? 'Try adjusting your search criteria' : 'Get started by creating your first account'}
              </p>
            </div>
          ) : (
            currentAccounts.map((account) => (
              <div 
                key={account._id} 
                className="bg-card text-card-foreground rounded-sm overflow-hidden border-1 border-muted-foreground/20 hover:shadow-lg transition-all duration-300 hover:border-primary/20 group bg-muted/50"
              >
                <div className="p-4 space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground mb-1">
                        Company Name
                      </p>
                      <p className="text-sm font-semibold truncate">
                        {account.name}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      {can(PERMISSIONS.EDIT_ACCOUNT) && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openEditDialog(account)}
                          className="h-8 w-8 p-0"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {can(PERMISSIONS.DELETE_ACCOUNT) && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openDeleteDialog(account)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      Email
                    </p>
                    <p className="text-sm font-medium truncate">
                      {account.email?.toLowerCase() || 'N/A'}
                    </p>
                  </div>

                  {/* Phone */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      Phone
                    </p>
                    <p className="text-sm truncate">
                      {account.phone || 'N/A'}
                    </p>
                  </div>

                  {/* GST Number */}
                  {account.gstNumber || account.gst_number ? (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        GST Number
                      </p>
                      <p className="text-sm truncate">
                        {account.gstNumber || account.gst_number}
                      </p>
                    </div>
                  ) : null}

                  {/* Date & Status */}
                  <div className="flex items-center justify-between pt-2 border-t border-muted-foreground/10">
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {new Date(account.createdAt).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      account.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' :
                      account.status === 'inactive' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100' :
                      'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100'
                    }`}>
                      {account.status || 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
  
      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 gap-4 ">
        {isLoading ? (
          <>
            <Skeleton className="h-5 w-48" />
            <div className="flex items-center gap-2 flex-wrap">
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-12" />
            </div>
          </>
        ) : (
          <>
            <div className="text-sm text-muted-foreground">
              Showing page {currentPage} of {totalPages} | Total: {filteredAccounts.length} accounts
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className="hover:bg-muted/50"
              >
                Previous
              </Button>
              {/* Show page 1 if not in range */}
              {currentPage > 2 && totalPages > 3 && (
                <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                    className={`${
                      currentPage === 1 
                        ? '!bg-black !text-white dark:!bg-white dark:!text-black font-semibold hover:!bg-black/90 dark:hover:!bg-white/90 border-black dark:border-white' 
                        : 'text-foreground hover:bg-muted/50'
                    }`}
              >
                1
              </Button>
                  {currentPage > 3 && <span className="px-2 text-muted-foreground">...</span>}
                </>
              )}
              {/* Show pages around current page */}
              {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return page;
              })
              .filter((page, index, arr) => arr.indexOf(page) === index) // Remove duplicates
              .filter(page => page >= 1 && page <= totalPages) // Ensure valid range
              .map(page => (
                <Button
                  key={page}
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page)}
                  className={`${
                    currentPage === page 
                      ? '!bg-black !text-white dark:!bg-white dark:!text-black font-semibold hover:!bg-black/90 dark:hover:!bg-white/90 border-black dark:border-white' 
                      : 'text-foreground hover:bg-muted/50'
                  }`}
                >
                  {page}
                </Button>
              ))}
              {/* Show last page if not in range */}
              {currentPage < totalPages - 2 && totalPages > 3 && (
                <>
                  {currentPage < totalPages - 3 && <span className="px-2 text-muted-foreground">...</span>}
                <Button
                  variant="outline"
                  size="sm"
                    onClick={() => handlePageChange(totalPages)}
                    className={`${
                      currentPage === totalPages 
                        ? '!bg-black !text-white dark:!bg-white dark:!text-black font-semibold hover:!bg-black/90 dark:hover:!bg-white/90 border-black dark:border-white' 
                        : 'text-foreground hover:bg-muted/50'
                    }`}
                >
                    {totalPages}
                </Button>
                </>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="hover:bg-muted/50"
              >
                Next
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card text-card-foreground rounded-lg p-6 max-w-md w-full mx-4 shadow-xl border border-border/20">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Delete Account
                </h3>
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-foreground">
                Are you sure you want to delete the account{' '}
                <span className="font-semibold text-foreground">
                  "{deleteDialog.account?.name}"
                </span>?
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                This will permanently remove the account and all associated data.
              </p>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={closeDeleteDialog}
                className="px-4 py-2"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAccount}
                className="px-4 py-2 bg-red-600 hover:bg-red-700"
              >
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Account Sheet */}
      <Sheet open={editDialog.isOpen} onOpenChange={(open) => !open && closeEditDialog()}>
        <SheetContent className="w-[400px] sm:w-[540px] p-3">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-xl">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <Pencil className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              Edit Account
            </SheetTitle>
            <SheetDescription className="text-muted-foreground">
              Update account information
            </SheetDescription>
          </SheetHeader>
          
          <div className="space-y-4 mt-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Company Name
                </label>
                <Input
                  value={editFormData.name}
                  onChange={(e) => handleEditFormChange('name', e.target.value)}
                  placeholder="Enter company name"
                  className="w-full text-muted-foreground border-1 border-muted-foreground/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email
                </label>
                <Input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => handleEditFormChange('email', e.target.value)}
                  placeholder="Enter email address"
                  className="w-full text-muted-foreground border-1 border-muted-foreground/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Phone
                </label>
                <Input
                  value={editFormData.phone}
                  onChange={(e) => handleEditFormChange('phone', e.target.value)}
                  placeholder="Enter phone number"
                  className="w-full text-muted-foreground border-1 border-muted-foreground/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  GST Number
                </label>
                <Input
                  value={editFormData.gstNumber}
                  onChange={(e) => handleEditFormChange('gstNumber', e.target.value)}
                  placeholder="Enter GST number"
                  className="w-full text-muted-foreground border-1 border-muted-foreground/20 bg-muted/50"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2  ">
                  Status
                </label>
                <select
                  value={editFormData.status}
                  onChange={(e) => handleEditFormChange('status', e.target.value)}
                  className="w-full px-3 py-2 rounded-md text-muted-foreground bg-muted/50 border-1 border-muted-foreground/20 focus:outline-none"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 justify-end mt-6">
            <Button
              variant="outline"
              onClick={closeEditDialog}
              className="px-4 py-2 text-muted-foreground border-1 border-muted-foreground/20 "
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleUpdateAccount}
              className="px-4 py-2 text-muted-foreground border-1 border-muted-foreground/20"
            >
              Update Account
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
